import { useRef, useState } from 'react';
import { Upload, FileText, Loader, Scan } from 'lucide-react';
import { colors } from '../../styles/colors';

function FileUploadZone({
  file,
  preview,
  scanning,
  scanProgress,
  onFileSelect,
  onScan,
  onReset
}) {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      // Créer un événement synthétique compatible avec handleFileSelect
      const syntheticEvent = {
        target: {
          files: droppedFiles
        }
      };
      onFileSelect(syntheticEvent);
    }
  };

  const styles = {
    uploadSection: {
      marginBottom: '2rem',
    },
    dropZone: {
      border: `2px dashed ${colors.border.medium}`,
      borderRadius: '8px',
      padding: '3rem 2rem',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s',
      backgroundColor: colors.background.light,
    },
    dropZoneDragging: {
      borderColor: colors.primary.teal,
      backgroundColor: '#E0F7F4',
      transform: 'scale(1.02)',
    },
    dropZoneText: {
      fontSize: '1.125rem',
      color: colors.text.dark,
      margin: '0 0 0.5rem 0',
      fontWeight: '500',
    },
    dropZoneSubtext: {
      fontSize: '0.875rem',
      color: colors.text.secondary,
      margin: 0,
    },
    filePreview: {
      backgroundColor: colors.background.white,
      borderRadius: '8px',
      padding: '1.5rem',
      border: `1px solid ${colors.border.light}`,
    },
    imagePreviewContainer: {
      marginBottom: '1.5rem',
      textAlign: 'center',
    },
    imagePreview: {
      maxWidth: '100%',
      maxHeight: '300px',
      borderRadius: '8px',
      border: `1px solid ${colors.border.light}`,
    },
    fileInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1.5rem',
      padding: '1rem',
      backgroundColor: colors.background.light,
      borderRadius: '8px',
    },
    fileDetails: {
      flex: 1,
    },
    fileName: {
      margin: '0 0 0.25rem 0',
      fontSize: '1rem',
      fontWeight: '500',
      color: colors.text.dark,
    },
    fileSize: {
      margin: 0,
      fontSize: '0.875rem',
      color: colors.text.secondary,
    },
    actions: {
      display: 'flex',
      gap: '1rem',
    },
    button: {
      flex: 1,
      padding: '0.75rem 1.5rem',
      borderRadius: '6px',
      fontSize: '1rem',
      fontWeight: '600',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    },
    primaryButton: {
      backgroundColor: colors.primary.teal,
      color: colors.background.white,
    },
    secondaryButton: {
      backgroundColor: colors.background.light,
      color: colors.text.dark,
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: colors.border.medium,
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    spinIcon: {
      animation: 'spin 1s linear infinite',
    },
    progressContainer: {
      marginBottom: '1rem',
    },
    progressBar: {
      width: '100%',
      height: '8px',
      backgroundColor: colors.background.light,
      borderRadius: '4px',
      overflow: 'hidden',
      marginBottom: '0.5rem',
    },
    progressFill: {
      height: '100%',
      backgroundColor: colors.primary.teal,
      transition: 'width 0.3s ease',
    },
    progressText: {
      margin: 0,
      fontSize: '0.875rem',
      color: colors.text.secondary,
      textAlign: 'center',
    },
  };

  return (
    <div style={styles.uploadSection}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={onFileSelect}
        style={{ display: 'none' }}
      />

      {!file ? (
        <div
          style={{
            ...styles.dropZone,
            ...(isDragging && styles.dropZoneDragging)
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Upload size={48} style={{ color: colors.primary.teal, marginBottom: '1rem' }} />
          <p style={styles.dropZoneText}>
            {isDragging ? 'Déposez le fichier ici' : 'Cliquez pour sélectionner un fichier'}
          </p>
          <p style={styles.dropZoneSubtext}>
            {isDragging ? '' : 'ou glissez-déposez un fichier • PDF, JPG, PNG'}
          </p>
        </div>
      ) : (
        <div style={styles.filePreview}>
          {preview && (
            <div style={styles.imagePreviewContainer}>
              <img src={preview} alt="Preview" style={styles.imagePreview} />
            </div>
          )}
          <div style={styles.fileInfo}>
            <FileText size={24} style={{ color: colors.primary.teal }} />
            <div style={styles.fileDetails}>
              <p style={styles.fileName}>{file.name}</p>
              <p style={styles.fileSize}>
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>

          {/* Barre de progression pendant le scan */}
          {scanning && scanProgress.total > 0 && (
            <div style={styles.progressContainer}>
              <div style={styles.progressBar}>
                <div style={{
                  ...styles.progressFill,
                  width: `${(scanProgress.current / scanProgress.total) * 100}%`
                }} />
              </div>
              <p style={styles.progressText}>
                Page {scanProgress.current} sur {scanProgress.total}
              </p>
            </div>
          )}

          <div style={styles.actions}>
            <button
              onClick={onScan}
              disabled={scanning}
              style={{
                ...styles.button,
                ...styles.primaryButton,
                ...(scanning && styles.buttonDisabled),
              }}
            >
              {scanning ? (
                <>
                  <Loader size={18} style={styles.spinIcon} />
                  {scanProgress.total > 0 ? (
                    `Scan page ${scanProgress.current}/${scanProgress.total}...`
                  ) : (
                    'Scan en cours...'
                  )}
                </>
              ) : (
                <>
                  <Scan size={18} />
                  Scanner le document
                </>
              )}
            </button>
            <button
              onClick={onReset}
              disabled={scanning}
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                ...(scanning && styles.buttonDisabled),
              }}
            >
              Changer de fichier
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FileUploadZone;
